import { useState, useEffect } from "react";
import { Star, Mail, Clock, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const Stars = ({ n }) => (
  <div className="flex items-center gap-0.5" data-testid="feedback-rating-display">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={15} strokeWidth={1.5} className={n >= i ? "text-amber-300 fill-amber-300" : "text-zinc-700"} />
    ))}
  </div>
);

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    api.get("/admin/feedback").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load feedback")).finally(() => setLoading(false));
  }, []);

  const rated = items.filter((f) => f.rating);
  const avg = rated.length ? (rated.reduce((s, f) => s + f.rating, 0) / rated.length) : 0;

  return (
    <div>
      <div className="mb-8">
        <div className="overline mb-2">Administration</div>
        <h1 className="font-dash text-3xl">Feedback</h1>
        <p className="text-zinc-500 mt-2 text-sm">Responses submitted through the mediation feedback form.</p>
      </div>
      {!loading && rated.length > 0 && (
        <div className="mb-8 flex flex-wrap items-center gap-6 rounded-xl border border-white/10 bg-[#0d0d0d] px-6 py-5" data-testid="feedback-summary">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-5xl text-silver">{avg.toFixed(1)}</span>
            <span className="text-zinc-500 text-sm">/ 5</span>
          </div>
          <div>
            <Stars n={Math.round(avg)} />
            <div className="text-zinc-500 text-xs mt-1.5">Average from {rated.length} rated response{rated.length !== 1 ? "s" : ""}{items.length !== rated.length ? ` · ${items.length} total` : ""}</div>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-zinc-500 text-sm py-10 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/12 py-16 text-center text-zinc-500" data-testid="feedback-empty">
          <MessageSquare size={28} strokeWidth={1} className="mx-auto mb-3 opacity-60" /> No feedback yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="admin-feedback-list">
          {items.map((f) => (
            <div key={f.id} className="rounded-xl border border-white/10 bg-[#0b0b0b] p-6" data-testid={`feedback-item-${f.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="text-white">{f.name}</span>
                    {f.role ? <span className="text-xs text-zinc-500 border border-white/12 rounded-full px-2.5 py-0.5">{f.role}</span> : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-1.5 text-xs text-zinc-500">
                    {f.email ? <a href={`mailto:${f.email}`} className="flex items-center gap-1.5 hover:text-white"><Mail size={12} /> {f.email}</a> : null}
                    <span className="flex items-center gap-1.5"><Clock size={12} /> {fmt(f.created_at)}</span>
                  </div>
                </div>
                {f.rating ? <Stars n={f.rating} /> : <span className="text-xs text-zinc-600">No rating</span>}
              </div>
              <p className="text-zinc-300 text-sm mt-4 leading-relaxed whitespace-pre-line">{f.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
